/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { PropTypes } from 'prop-types';
import paginationFactory from 'react-bootstrap-table2-paginator';
import BootstrapTable from 'react-bootstrap-table-next';

// Redux Imports
import { useSelector, useDispatch } from 'react-redux';
import {
   troubleEndSelector,
   troubleStartSelector,
   hashSelector,
   troubleIntervalSelector,
   alertIdSelector,
   troubleModeSelector,
   troubleTimespanManuallySetSelector,
   alertFineEndSelector,
   alertFineStartSelector,
} from '../../../../redux/selectors/selectors';
import { setTroubleshootingSelectedAlert } from '../../../../redux/actions/general';
import { URL_PARAMS } from '../../../../urlSync/urlParamNames';

// External Libraries
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import moment from 'moment';
import axios from 'axios';

// Custom Imports
import { CHRONOLOGICAL_DATE_FORMAT, CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT } from '../constants';
import { ANALYTICS_API_URL } from '../../../../constants/config';
import { ALERT_FINE_START_REDUCTION_IN_MINUTES, ALERT_FINE_END_EXTENSION_IN_MINUTES } from './widgets/constants';
import { getHttpHeader } from '../../../../util/FetchTools';
import {
   BadgeListItem,
   BottomMargin,
   Description,
   LightHeadline,
   Notification,
   SimpleHorizontalSeparator,
} from '../../../../styledComponents/styledComponents';
import { getIntervalEnd, getIntervalStart } from '../general/DataProcessing';
// the severity vocabulary of the alerting, shared with the stream player so the ranking cannot drift apart
import { DEFAULT_SEVERITY_COLOR, severityRank } from '../../../root/contentArea/StreamPlayer/streamAlerts';
import { getAlertTitlesByPrecedence } from './widgets/alerting/AlertDetailsMapper';
import AlertWidgets from './widgets/alerting/AlertWidgets';
import ExpandedAlertCell from './ExpandedAlertCell';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import BlockSpinner from '../../../common/BlockSpinner/BlockSpinner';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import { E2E } from '../../../../constants/general';

export function mergeStreamDetails(connectionsDetails) {
   const streamDetailsProps = [
      'provider',
      'city',
      'countryName',
      'countryCode',
      'height',
      'width',
      'ip',
      'server',
      'connectionId',
   ];
   const streamDetailsResult = {};
   const streamDetailsSets = {};
   streamDetailsProps.forEach((prop) => (streamDetailsSets[prop] = new Set()));
   connectionsDetails.forEach((connectionDetails) => {
      streamDetailsProps.forEach((prop) => {
         if (connectionDetails[prop]) {
            streamDetailsSets[prop].add(connectionDetails[prop]);
         }
      });
   });
   // remove '' from sets like Set('', 'foo') => Set('foo')
   Object.keys(streamDetailsSets).forEach((prop) => {
      if (streamDetailsSets[prop].has('') && streamDetailsSets[prop].size > 1) {
         streamDetailsSets[prop].delete('');
      }
   });

   Object.keys(streamDetailsSets).forEach((prop) => {
      streamDetailsResult[prop] = Array.from(streamDetailsSets[prop]);
   });
   return streamDetailsResult;
}

// One tooltip for the cells that keep their remaining values behind a '+n'. The native title attribute is
// not enough for them: how long the browser waits before showing it is the browser's to decide, and it waits
// long enough that the '+n' reads as unexplained - which is the whole thing the tooltip is there to explain.
const CELL_TOOLTIP_ID = 'alert-table-cell';

// Secondary cell text. Deliberately not bootstrap's 'text-muted': that is a fixed grey, which disappears
// against the blue background of the selected row. Inheriting the row colour keeps it readable on both.
const SECONDARY_TEXT_STYLE = { opacity: 0.7 };

// The badge palette deviates from the shared SEVERITY_COLORS on purpose: a badge sits inside the row, which
// turns bg-primary blue while it is selected, and the shared blue of 'minor' would disappear on it. Only the
// presentation differs - the vocabulary and its ranking come from streamAlerts.
const SEVERITY_STYLE = {
   critical: { $bgColor: 'rgba(233,43,43,0.85)', $color: 'rgb(255,255,255)' },
   moderate: { $bgColor: 'rgba(237,125,14,0.85)', $color: 'rgb(255,255,255)' },
   minor: { $bgColor: 'rgba(200,200,200,1)', $color: 'rgba(0,0,0,0.9)' },
};

// a severity the frontend does not know yet still gets a readable badge instead of an unstyled one
const DEFAULT_SEVERITY_STYLE = { $bgColor: DEFAULT_SEVERITY_COLOR, $color: 'rgb(255,255,255)' };

// worst first, so the badge shows the worst state a notification ever reached. Ranked by the shared helper,
// which sorts an unknown severity last - indexOf would rank it as -1, i.e. ahead of 'critical'.
function sortBySeverity(severities) {
   return [...(severities || [])].sort((a, b) => severityRank(a) - severityRank(b));
}

const columns = [
   {
      dataField: 'id',
      text: 'Alert ID',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
   },
   {
      // A notification accumulates every severity it ever carried, so joining them reads as if several
      // alerts were active at once. The badge shows the worst one, the rest stays reachable as a count.
      dataField: 'severities',
      text: 'Severity',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
      formatter: (cell) => {
         const sorted = sortBySeverity(cell);
         if (sorted.length === 0) return '-';
         const [highest, ...rest] = sorted;
         return (
            <span
               data-tooltip-id={rest.length > 0 ? CELL_TOOLTIP_ID : undefined}
               data-tooltip-content={rest.length > 0 ? sorted.join(', ') : undefined}
            >
               <BadgeListItem
                  $fontSizePx={11}
                  $marginRightPx={rest.length > 0 ? 4 : 0}
                  {...(SEVERITY_STYLE[highest] || DEFAULT_SEVERITY_STYLE)}
               >
                  {highest}
               </BadgeListItem>
               {rest.length > 0 && <small style={SECONDARY_TEXT_STYLE}>{`+${rest.length}`}</small>}
            </span>
         );
      },
   },
   {
      dataField: 'status',
      text: 'Status',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
   },
   {
      // The concrete alert rather than the type label: the type cannot say which effect was detected for the
      // types that carry several alerts (maxFrameDelay: persistent delay / ull / stalls). A notification can
      // have carried more than one over its lifetime - the most relevant one is named, the others are counted
      // and listed on hover, so the cell stays one line. The type stays visible for the selected alert, as
      // the 'About' badge of the details panel. Falls back to the type label for an unknown code.
      dataField: 'label',
      text: 'Alert',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
      formatter: (cell, row) => {
         const [primaryTitle, ...furtherTitles] = getAlertTitlesByPrecedence(row.codes);
         if (primaryTitle === undefined) return cell;
         return (
            <span
               data-tooltip-id={furtherTitles.length > 0 ? CELL_TOOLTIP_ID : undefined}
               data-tooltip-content={furtherTitles.length > 0 ? [primaryTitle, ...furtherTitles].join(', ') : undefined}
            >
               {primaryTitle}
               {furtherTitles.length > 0 && <small style={SECONDARY_TEXT_STYLE}>{` +${furtherTitles.length}`}</small>}
            </span>
         );
      },
   },
   {
      dataField: 'codes',
      text: 'Alert Codes',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
      formatter: (cell) => cell.join(' | '),
   },
   {
      dataField: 'first',
      text: 'Started at',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
      formatter: (cell) => moment.utc(cell).format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT),
   },
   {
      dataField: 'last',
      text: 'Last seen at',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
      formatter: (cell) => moment.utc(cell).format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT),
   },
   {
      dataField: 'duration',
      text: 'Duration',
      style: { textAlign: 'center', cursor: 'pointer' },
      headerStyle: { textAlign: 'center', cursor: 'pointer' },
   },
];

function AlertsTable({ streamName, created }) {
   const userInfo = browserStorage.getUserInfo();
   const isAdmin = userInfo.isAdmin;

   // --------------------------
   // ## React useDispatch hook (REDUX)
   // --------------------------

   const dispatch = useDispatch();

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const start = useSelector(troubleStartSelector);
   const end = useSelector(troubleEndSelector);
   const hash = useSelector(hashSelector);
   const interval = useSelector(troubleIntervalSelector);
   const alertId = useSelector(alertIdSelector);
   const mode = useSelector(troubleModeSelector);
   const hasTimespanManuallyBeenSet = useSelector(troubleTimespanManuallySetSelector);

   const alertFineStart = useSelector(alertFineStartSelector);
   const alertFineEnd = useSelector(alertFineEndSelector);

   // --------------------------
   // ## React useState hooks
   // --------------------------

   const [newPage, setNewPage] = useState(1);
   const [tableData, setTableData] = useState([]);
   const [selectedAlert, setSelectedAlert] = useState(null);
   const [selectedAlertIndex, setSelectedAlertIndex] = useState(-1);

   // --------------------------
   // ## React useMemo hooks
   // --------------------------

   const headerObj = useMemo(() => getHttpHeader(hash), [hash]);

   // --------------------------
   // ## React useEffect hooks
   // --------------------------

   useEffect(() => {
      let newAlertIndex = tableData.findIndex((alert) => alert.id === alertId);
      setSelectedAlertIndex(newAlertIndex);
      if (newAlertIndex === -1) {
         setSelectedAlert(null);
         if (alertId !== '' && tableData.length > 0) {
            toast.error('Alert ID ' + alertId + ' not found in the given time range!');
            dispatch(
               setTroubleshootingSelectedAlert(
                  {
                     alertId: '',
                     alertFineStart: '',
                     alertFineEnd: '',
                  },
                  {
                     [URL_PARAMS.ALERT_ID]: '',
                     [URL_PARAMS.ALERT_FINE_START]: '',
                     [URL_PARAMS.ALERT_FINE_END]: '',
                  },
               ),
            );
         }
      } else {
         let quotient = Math.floor((newAlertIndex + 1) / 5);
         let remains = Math.floor((newAlertIndex + 1) % 5);
         setNewPage(remains === 0 ? quotient : quotient + 1);

         setSelectedAlert(tableData[newAlertIndex]);
      }
   }, [tableData, alertId, dispatch]);

   useEffect(() => {
      if (alertId !== '' && alertFineStart === '' && alertFineEnd === '') {
         const selectedAlert = tableData.find((alert) => alert.id === alertId);
         if (selectedAlert !== undefined) {
            const adjustedFineStart = moment
               .utc(selectedAlert.first)
               .subtract(ALERT_FINE_START_REDUCTION_IN_MINUTES, 'minutes')
               .format();
            // prettier-ignore
            const adjustedFineEnd = moment.utc(selectedAlert.last).add(ALERT_FINE_END_EXTENSION_IN_MINUTES, 'minutes').format();

            dispatch(
               setTroubleshootingSelectedAlert(
                  {
                     alertId: selectedAlert.id,
                     alertFineStart: getIntervalStart(adjustedFineStart, interval.value),
                     alertFineEnd: getIntervalEnd(adjustedFineEnd, interval.value),
                  },
                  {
                     [URL_PARAMS.ALERT_ID]: selectedAlert.id,
                     [URL_PARAMS.ALERT_FINE_START]: getIntervalStart(adjustedFineStart, interval.value),
                     [URL_PARAMS.ALERT_FINE_END]: getIntervalEnd(adjustedFineEnd, interval.value),
                  },
               ),
            );
         }
      }
   }, [tableData, alertId, dispatch, alertFineStart, alertFineEnd, interval]);

   // --------------------------
   // ## React useQuery hooks
   // --------------------------

   const FETCH_ALERTS_URL = `${ANALYTICS_API_URL}/v2/alerting/ingest/find`;

   const { error, isLoading, isFetching } = useQuery({
      enabled: (streamName !== '' || alertId !== '') && !hasTimespanManuallyBeenSet,
      queryKey: ['fetchAlertsByStreamName', streamName, start, end],
      queryFn: () => {
         return axios
            .get(FETCH_ALERTS_URL, {
               headers: headerObj,
               params: {
                  streams: streamName,
                  from: moment.utc(start).format(),
                  to: moment.utc(end).format(),
               },
            })
            .then((res) => res.data);
      },
      onSuccess: (res) => {
         if (res.data.length > 0) {
            const dataToShow = res.data.map((alert) => {
               return {
                  id: alert.id,
                  severities: alert.severities, // array
                  status: alert.status,
                  type: alert.type,
                  label: alert.label,
                  codes: alert.codes, // array
                  isAudioStream: alert.isAudioStream, // maxFrameDelay alerts only
                  first: alert.first,
                  last: alert.last,
                  duration: alert.duration,
                  servers: alert.servers, // array
                  causes: alert.causes, // array
                  howToFix: alert.howToFix, // array
                  details: alert.details, // { messages: '', details?: {} }
                  messages: alert.messages,
                  connectionsDetails: alert.connectionsDetails, // array
                  // connectionsDetails: [{server,city, provider,countryCode,countryName,ip, width, height, connectionId, start, end}]
                  streamDetails: mergeStreamDetails(alert.connectionsDetails), // object
               };
            });

            setTableData(dataToShow);
         }
      },
      onError: (error) => {
         toast.error(getUnifiedErrorObject(error).message);
      },
      retry: false,
      refetchOnWindowFocus: false,
   });

   // --------------------------
   // ## React useCallback hooks
   // --------------------------

   // info: used paginationLib does not intend to set page programatically, unless BootstrapTable is destroyed and created from scratch for each re-rendering
   const TableElement = useCallback(() => {
      function handleRowSelection(row, isExpand) {
         if (isExpand) {
            // prettier-ignore
            const adjustedFineStart = moment
               .utc(row.first)
               .subtract(ALERT_FINE_START_REDUCTION_IN_MINUTES, 'minutes')
               .format();
            // prettier-ignore
            const adjustedFineEnd = moment.utc(row.last).add(ALERT_FINE_END_EXTENSION_IN_MINUTES, 'minutes').format();

            dispatch(
               setTroubleshootingSelectedAlert(
                  {
                     alertId: row.id,
                     alertFineStart: getIntervalStart(adjustedFineStart, interval.value),
                     alertFineEnd: getIntervalEnd(adjustedFineEnd, interval.value),
                  },
                  {
                     [URL_PARAMS.ALERT_ID]: row.id,
                     [URL_PARAMS.ALERT_FINE_START]: getIntervalStart(adjustedFineStart, interval.value),
                     [URL_PARAMS.ALERT_FINE_END]: getIntervalEnd(adjustedFineEnd, interval.value),
                  },
               ),
            );
         } else {
            dispatch(
               setTroubleshootingSelectedAlert(
                  {
                     alertId: '',
                     alertFineStart: '',
                     alertFineEnd: '',
                  },
                  {
                     [URL_PARAMS.ALERT_ID]: '',
                     [URL_PARAMS.ALERT_FINE_START]: '',
                     [URL_PARAMS.ALERT_FINE_END]: '',
                  },
               ),
            );
         }
      }
      const InteractionHandler = {
         parentClassName: () => 'bg-primary',
         renderer: (row) => <ExpandedAlertCell row={row} mode={mode} />,
         onlyOneExpanding: true,
         expanded: [alertId],
         onExpand: (row, isExpand, rowIndex) => handleRowSelection(row, isExpand, rowIndex),
      };

      const paginationOptions = {
         page: newPage,
         sizePerPage: 5,
         sizePerPageList: [
            { text: '5', value: 5 },
            { text: '10', value: 10 },
            { text: '15', value: 15 },
            { text: '20', value: 20 },
         ],
         paginationSize: 3,
         lastPageText: '>>',
         firstPageText: '<<',
         showTotal: true,
         alwaysShowAllBtns: false,
      };

      return (
         <BootstrapTable
            id='AlertTable'
            keyField='id'
            hover
            striped
            bootstrap4
            data={tableData}
            columns={columns}
            expandRow={InteractionHandler}
            defaultSorted={[
               {
                  dataField: 'first',
                  order: 'asc',
               },
            ]}
            pagination={paginationFactory(paginationOptions)}
            classes={`table-responsive-${mode === E2E ? 'xxxl' : 'md'}`}
         />
      );
   }, [alertId, dispatch, interval.value, mode, newPage, tableData]);

   // --------------------------
   // ## Constants / Variables / Functions
   // --------------------------

   const tableIsEmpty = tableData?.length === 0;

   const table = document.getElementById('AlertTable');
   if (table && isAdmin) {
      table.addEventListener('click', (event) => {
         if (event.target.tagName === 'TD') {
            const content = event.target.textContent;
            navigator.clipboard.writeText(content);

            // Popup message
            const cellIndex = event.target.cellIndex;
            const columnHeader = table.tHead.rows[0].cells[cellIndex];
            const columnName = columnHeader.textContent;
            const popup = document.getElementById('popup');
            if (popup) {
               popup.style.display = 'block';
               popup.style.left = event.clientX + 'px';
               popup.style.top = event.clientY + 'px';
               popup.textContent = 'Copied ' + columnName + '!';
               setTimeout(() => {
                  popup.style.display = 'none';
               }, 1000);
            }
         }
      });

      // Hover Expansion - Not used yet

      // const cells = table.querySelectorAll('td');
      // cells.forEach((td) => {
      //    td.classList.add('functional-td');
      //    let timer;
      //    let hoverTime = 0;

      //    td.addEventListener('mouseenter', () => {
      //       timer = setInterval(() => {
      //          hoverTime++;
      //          td.dataset.hoverTime = hoverTime;
      //       }, 1000);
      //    });

      //    td.addEventListener('mouseleave', () => {
      //       console.log(hoverTime);

      //       clearInterval(timer);
      //       hoverTime = 0;
      //       td.dataset.hoverTime = hoverTime;
      //    });
      // });
   }

   const processedError = error ? getUnifiedErrorObject(error) : error;

   return (
      <>
         {/* <ClipBoardPopUp id='popup' /> */}
         <SimpleHorizontalSeparator spacePx={20} border={'1px dotted  rgb(180,180,180)'} />
         <LightHeadline marginBottomPx={3}>
            {`Alerts of stream '${streamName}' (created on ${moment.utc(created).format(CHRONOLOGICAL_DATE_FORMAT)})`}
         </LightHeadline>
         {isLoading || isFetching ? (
            <BlockSpinner paddingTopBottomPx={50} />
         ) : (
            <>
               {processedError ? (
                  <Notification indentPx={0}>
                     {`${processedError.message ? processedError.message : processedError}`}
                  </Notification>
               ) : !tableIsEmpty ? (
                  <>
                     <Description marginBottomPx={15} marginLeftPx={0} alpha={0.5}>
                        Select an alert to get metrics
                     </Description>
                     <BottomMargin pxSize={20}>
                        <TableElement />
                        <ReactTooltip
                           id={CELL_TOOLTIP_ID}
                           place={'top'}
                           className={'reduced-line-height bring-on-top'}
                           backgroundColor={'rgba(0,0,0,0.9)'}
                           textColor={'rgb(240,240,240)'}
                           style={{ textAlign: 'center', maxWidth: '400px' }}
                        />
                     </BottomMargin>

                     {selectedAlertIndex !== -1 && selectedAlert !== null && (
                        <AlertWidgets selectedAlert={selectedAlert} streamName={streamName} />
                     )}
                  </>
               ) : (
                  <>
                     <LightHeadline marginBottomPx={8} marginTopPx={20} $fontSizePx={14.5}>
                        {'No alerts detected for this stream within the specified time range.'}
                     </LightHeadline>
                  </>
               )}
            </>
         )}
      </>
   );
}

AlertsTable.propTypes = {
   streamName: PropTypes.string,
   created: PropTypes.string,
};

export default AlertsTable;
